/** The admin route table: each route's path segment and title (TopNav, PageHeader, breadcrumbs). */
export const paths = {
  ROOT: {
    path: "/",
    title: "Home",
  },
  ORGANIZATIONS: {
    path: "organizations",
    title: "Organizations",
  },
  ORGANIZATION: {
    path: ":organizationId",
    title: "Organization",
  },
  ACCESS_CONTROL: {
    path: "rbac",
    title: "Access Control",
  },
  GLOBAL_CONTENTS: {
    path: "global-content",
    title: "Global Content",
    // Only its :contentId pages exist, so its crumb is not a link
    hasPage: false,
  },
  GLOBAL_CONTENT: {
    path: ":contentId",
    title: "Global Content Summary",
  },
  STAKEHOLDERS: {
    path: "stakeholders",
    title: "Stakeholders",
    defaultQueryParams: {
      page: 1,
      pageSize: 20,
    },
  },
  STAKEHOLDER_DETAILS: {
    path: ":stakeholderId",
    title: "Details",
  },
  SUPERPROMPTS: {
    path: "superprompts",
    title: "Super Prompts",
  },
  SCRAPERS: {
    path: "Scrapers",
    title: "Scrapers",
  },
  SCRAPER_DETAILS: {
    path: ":scraperId",
    title: "Details",
  },
  INSIGHT_TOOLS: {
    path: "insight-tools",
    title: "Insight tools",
  },
  SPECIAL_PERMISSIONS: {
    path: "special-permissions",
    title: "Special permissions",
  },
  MANAGE_ASSETS: {
    path: "manage-assets",
    title: "Manage assets",
  },
  GENIE_KNOWLEDGE: {
    path: "genie-knowledge",
    title: "Genie behaviour & knowledge",
  },
  GENIE_PLUS_USAGE: {
    path: "genie-plus-usage",
    title: "GeniePlus Usage",
  },
  PIPELINE: {
    path: "pipeline",
    title: "Data pipeline",
    // Its index forwards to Today: the crumb is a link there (FB-7 #3).
  },
  /** D66 (FB-41): the landing page, what needs attention now and the headline numbers. */
  PIPELINE_TODAY: {
    path: "today",
    title: "Today",
  },
  // A "source" in the API is a Collector in the admin (D31; it was a Monitor, D28): only the words change.
  PIPELINE_COLLECTORS: {
    path: "collectors",
    // D66: building and watching collectors are one tab.
    title: "Collectors",
  },
  /** Before UX-3, the Workspaces view (`?workspace=&folder=`): now the explorer's tree. */
  PIPELINE_LEGACY_WORKSPACES: {
    path: "workspaces",
    title: "Collectors",
  },
  /** The address the Live sub-tab had briefly: now All collectors' Live filter (L7.2). */
  PIPELINE_LEGACY_COLLECTORS_LIVE: {
    path: "live",
    title: "Collectors",
  },
  /** Before UX-3, the New collector page: now its editor in the explorer's pane. */
  PIPELINE_LEGACY_COLLECTOR_NEW: {
    path: "new",
    title: "New collector",
  },
  /** Before UX-3, a collector's own page: now the explorer's pane (`?item=`). */
  PIPELINE_LEGACY_COLLECTOR: {
    path: ":sourceId",
    title: "Collector",
  },
  /** C7: the term set library a collector's filter picks from, under Collectors (D66). */
  PIPELINE_TERM_SETS: {
    path: "term-sets",
    title: "Term sets",
  },
  PIPELINE_TERM_SET_NEW: {
    path: "new",
    title: "New term set",
  },
  PIPELINE_TERM_SET: {
    path: ":termSetId",
    title: "Term set",
  },
  /** D66: every run, sandbox and real, with Skipped and Workers beside it (was Operations). */
  PIPELINE_RUNS: {
    path: "runs",
    title: "Runs",
  },
  PIPELINE_RUN: {
    path: ":runId",
    title: "Run",
  },
  PIPELINE_TASK: {
    path: "tasks/:taskId",
    title: "Task",
  },
  /** D56: the items one run skipped (`/pipeline/runs/:runId/skipped`). */
  PIPELINE_RUN_SKIPPED: {
    path: "skipped",
    title: "Skipped",
  },
  /** D56: the items steps skipped, across runs (`/pipeline/runs/skipped`). */
  PIPELINE_RUNS_SKIPPED: {
    path: "skipped",
    title: "Skipped",
  },
  PIPELINE_RUNS_WORKERS: {
    path: "workers",
    title: "Workers & queue",
  },
  /** UX-3 P6 (FB-43 round 9): every page any collector found, read or handed off. */
  PIPELINE_OUTPUTS: {
    path: "outputs",
    title: "Outputs",
  },
  PIPELINE_PROVIDERS: {
    path: "providers",
    title: "Providers",
  },
  /** The Providers tab's own page (FB-41 #1): the providers added, by kind. */
  PIPELINE_PROVIDERS_DIRECTORY: {
    path: "",
    title: "Your providers",
    crumbOfIndex: true,
  },
  /** Providers › Catalog: every kind we can connect, and where adding starts. */
  PIPELINE_PROVIDERS_CATALOG: {
    path: "catalog",
    title: "Catalog",
  },
  /** D66: usage, limits and spend (was the Providers tab's Health & usage). */
  PIPELINE_USAGE: {
    path: "usage",
    title: "Usage",
  },
  // ── Old addresses, each forwarded to its new place with its query kept ──
  PIPELINE_LEGACY_OVERVIEW: {
    path: "overview",
    title: "Today",
  },
  PIPELINE_LEGACY_OPERATIONS: {
    path: "operations/*",
    title: "Runs",
  },
  PIPELINE_LEGACY_WORKERS: {
    path: "workers",
    title: "Workers & queue",
  },
  PIPELINE_LEGACY_SEARCH: {
    path: "search",
    title: "Today",
  },
  PIPELINE_LEGACY_PROVIDERS_ADD: {
    path: "add",
    title: "Add provider",
  },
  PIPELINE_LEGACY_PROVIDERS_HEALTH: {
    path: "health",
    title: "Usage",
  },
  PIPELINE_LEGACY_SANDBOX_RUNS: {
    path: "sandbox-runs",
    title: "Runs",
  },
  PIPELINE_LEGACY_TEMPLATES: {
    path: "templates/*",
    title: "Term sets",
  },
  /** The address collectors had before D28 (`/pipeline/sources/...`): forwarded. */
  PIPELINE_LEGACY_SOURCES: {
    path: "sources/*",
    title: "Collectors",
  },
  /** The address term sets had before D49 (`/pipeline/term-sets/...`): forwarded. */
  PIPELINE_LEGACY_TERM_SETS: {
    path: "term-sets/*",
    title: "Term sets",
  },
  /** The address collectors had before D31 (`/pipeline/monitors/...`): forwarded. */
  PIPELINE_LEGACY_MONITORS: {
    path: "monitors/*",
    title: "Collectors",
  },
};
